import { DOCUMENT } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  NavigationCancel, NavigationEnd, NavigationError, NavigationSkipped, NavigationStart,
  PreloadingStrategy, Route, Router,
} from '@angular/router';
import { Observable, Subscriber, Subscription, finalize, isObservable, of } from 'rxjs';
import { AuthService } from '@core/services/auth.service';

type ChildrenFactory = NonNullable<Route['loadChildren']>;
type ComponentFactory = NonNullable<Route['loadComponent']>;

interface PreloadTask {
  route: Route;
  load: () => Observable<unknown>;
  observer: Subscriber<unknown>;
  priority: number;
}

interface FactoryLease {
  invoked: boolean;
  release(): void;
}

class PreloadInterruptedError extends Error {
  constructor() {
    super('Preload cancelled before lazy factory invocation.');
  }
}

/**
 * Admit opted-in routes at fresh idle opportunities, with at most two unsettled
 * preload-originated lazy factory results. Foreground loads are never queued.
 *
 * Angular's load() observable also waits for recursive child preloading; holding
 * a permit for that observable would deadlock descendants. Instead, instrument
 * the public factories only during Angular's synchronous load/subscribe setup.
 * The installed RouterPreloader integration test pins this Angular contract.
 *
 * A non-abortable Promise retains its permit after outer cancellation. Eventual
 * queue progress therefore assumes admitted factory results eventually settle.
 * This bounds factory results, not import subrequests or foreground network work.
 */
@Injectable({ providedIn: 'root' })
export class CustomPreloadingStrategy implements PreloadingStrategy, OnDestroy {
  private static readonly MAX_FACTORY_RESULTS = 2;
  private static readonly IDLE_TIMEOUT_MS = 1_000;

  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly queue: PreloadTask[] = [];
  private readonly tasks = new Set<PreloadTask>();
  private readonly navigationIds = new Set<number>();
  private readonly navigationSubscription: Subscription;
  private availablePermits = CustomPreloadingStrategy.MAX_FACTORY_RESULTS;
  private idleHandle: number | undefined;
  private timerHandle: ReturnType<typeof setTimeout> | undefined;
  private idleGeneration = 0;
  private settingUpFactories = false;
  private destroyed = false;

  constructor() {
    const current = this.router.currentNavigation();
    if (current) this.navigationIds.add(current.id);
    this.navigationSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.navigationIds.add(event.id);
        this.cancelIdle();
      } else if (
        event instanceof NavigationEnd || event instanceof NavigationCancel ||
        event instanceof NavigationError || event instanceof NavigationSkipped
      ) {
        this.navigationIds.delete(event.id);
        this.schedule();
      }
    });
  }

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    const priority = this.priorityFor(route);
    if (this.destroyed || priority === undefined || !this.authService.isAuthenticated()) {
      return of(null);
    }
    return new Observable(observer => {
      if (this.destroyed || !this.authService.isAuthenticated()) {
        observer.next(null);
        observer.complete();
        return;
      }
      const task: PreloadTask = { route, load, observer, priority };
      this.tasks.add(task);
      this.queue.push(task);
      // Stable sort preserves FIFO among routes with equal priority.
      this.queue.sort((left, right) => left.priority - right.priority);
      this.schedule();
      return () => {
        this.tasks.delete(task);
        const index = this.queue.indexOf(task);
        if (index >= 0) this.queue.splice(index, 1);
        if (this.queue.length === 0) this.cancelIdle();
        this.schedule();
      };
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.navigationSubscription.unsubscribe();
    this.cancelIdle();
    for (const task of [...this.tasks]) task.observer.complete();
  }

  private priorityFor(route: Route): number | undefined {
    if (!route.data || route.data['preload'] === false) return undefined;
    switch (route.data['priority']) {
      case 'critical': return 0;
      case 'high': return 1;
      case 'medium': return 2;
      default: return undefined;
    }
  }

  private factoryCount(route: Route): number {
    return (route.loadChildren ? 1 : 0) + (route.loadComponent ? 1 : 0);
  }

  private schedule(): void {
    if (
      this.destroyed || this.settingUpFactories || this.navigationIds.size > 0 ||
      this.idleHandle !== undefined || this.timerHandle !== undefined ||
      this.queue.length === 0 || this.factoryCount(this.queue[0].route) > this.availablePermits
    ) return;

    const generation = this.idleGeneration;
    const run = (deadline?: IdleDeadline) => {
      if (this.destroyed || generation !== this.idleGeneration) return;
      this.idleHandle = undefined;
      this.timerHandle = undefined;
      if (!deadline || deadline.didTimeout || deadline.timeRemaining() > 0) {
        this.admit();
      }
      this.schedule();
    };
    const view = this.document.defaultView;
    if (view && typeof view.requestIdleCallback === 'function') {
      this.idleHandle = view.requestIdleCallback(run, {
        timeout: CustomPreloadingStrategy.IDLE_TIMEOUT_MS,
      });
    } else {
      this.timerHandle = setTimeout(run, 0);
    }
  }

  private cancelIdle(): void {
    this.idleGeneration += 1;
    if (this.idleHandle !== undefined) {
      this.document.defaultView?.cancelIdleCallback?.(this.idleHandle);
      this.idleHandle = undefined;
    }
    if (this.timerHandle !== undefined) {
      clearTimeout(this.timerHandle);
      this.timerHandle = undefined;
    }
  }

  private admit(): void {
    if (this.destroyed || this.navigationIds.size > 0 || this.queue.length === 0) return;
    const task = this.queue[0];
    if (task.observer.closed) {
      this.queue.shift();
      return;
    }
    if (!this.authService.isAuthenticated()) {
      this.queue.shift();
      task.observer.next(null);
      task.observer.complete();
      return;
    }
    const childrenFactory = task.route.loadChildren;
    const componentFactory = task.route.loadComponent;
    const permits = (childrenFactory ? 1 : 0) + (componentFactory ? 1 : 0);
    if (permits > this.availablePermits) return;

    this.queue.shift();
    this.availablePermits -= permits;
    const childrenLease = childrenFactory ? this.createLease() : undefined;
    const componentLease = componentFactory ? this.createLease() : undefined;
    const wrappedChildren = childrenFactory && childrenLease
      ? this.instrumentFactory(childrenFactory, childrenLease, task) : undefined;
    const wrappedComponent = componentFactory && componentLease
      ? this.instrumentFactory(componentFactory, componentLease, task) : undefined;

    this.settingUpFactories = true;
    try {
      if (wrappedChildren) task.route.loadChildren = wrappedChildren;
      if (wrappedComponent) task.route.loadComponent = wrappedComponent;
      const inner = task.load().subscribe({
        next: value => task.observer.next(value),
        error: error => {
          if (error instanceof PreloadInterruptedError) {
            task.observer.next(null);
            task.observer.complete();
          } else {
            task.observer.error(error);
          }
        },
        complete: () => task.observer.complete(),
      });
      // add() also immediately tears down inner if synchronous completion or
      // cancellation closed the outer observer during load/subscribe setup.
      task.observer.add(inner);
    } catch (error) {
      if (error instanceof PreloadInterruptedError) {
        task.observer.next(null);
        task.observer.complete();
      } else {
        task.observer.error(error);
      }
    } finally {
      if (wrappedChildren && task.route.loadChildren === wrappedChildren) {
        task.route.loadChildren = childrenFactory;
      }
      if (wrappedComponent && task.route.loadComponent === wrappedComponent) {
        task.route.loadComponent = componentFactory;
      }
      if (childrenLease && !childrenLease.invoked) childrenLease.release();
      if (componentLease && !componentLease.invoked) componentLease.release();
      this.settingUpFactories = false;
      this.schedule();
    }
  }

  private createLease(): FactoryLease {
    let released = false;
    return {
      invoked: false,
      release: () => {
        if (released) return;
        released = true;
        this.availablePermits += 1;
        this.schedule();
      },
    };
  }

  private instrumentFactory(
    factory: ComponentFactory, lease: FactoryLease, task: PreloadTask
  ): ComponentFactory;
  private instrumentFactory(
    factory: ChildrenFactory, lease: FactoryLease, task: PreloadTask
  ): ChildrenFactory;
  private instrumentFactory(
    factory: ChildrenFactory, lease: FactoryLease, task: PreloadTask
  ): ChildrenFactory {
    return () => {
      if (lease.invoked) throw new Error('A preload factory was invoked more than once during setup.');
      if (
        this.destroyed || task.observer.closed || this.navigationIds.size > 0 ||
        !this.authService.isAuthenticated()
      ) throw new PreloadInterruptedError();
      lease.invoked = true;
      try {
        // Preserve the original Route receiver and Angular injection context.
        const result = factory.call(task.route);
        if (isObservable(result)) return result.pipe(finalize(lease.release));
        // Angular itself normalizes synchronous values and Promises this way.
        // Keep the rejection handled even when its outer subscription is gone.
        return Promise.resolve(result).then(
          value => { lease.release(); return value; },
          error => { lease.release(); throw error; },
        );
      } catch (error) {
        lease.release();
        throw error;
      }
    };
  }
}
